import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listAttributeGroupRecords, syncAttributeGroupsFromApi } from "@/lib/catalog/attributeGroupsStore";
import { isAxiosError } from "axios";
import {
  codeExists,
  createCatalogAttribute,
  deleteCatalogAttribute,
  listCatalogAttributeRecords,
  syncCatalogAttributesFromApi,
  updateCatalogAttribute,
  type CatalogAttributeRecord,
} from "@/lib/catalog/catalogAttributesStore";
import { removeLinksForAttribute } from "@/lib/catalog/categoryAttributeLinksStore";
import type { AttributeVisibilityOperator } from "@/lib/catalog/attributeVisibility";
import type { AttributeType, ID } from "@/lib/catalog/types";
import { slugify } from "@/lib/slugify";
import { useGridPagination } from "@/hooks/useGridPagination";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { Braces, CalendarIcon, CircleDot, Edit, Eye, Filter, LayoutGrid, List, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "@/components/ui/sonner";

const ATTRIBUTE_TYPES: AttributeType[] = [
  "text",
  "textarea",
  "number",
  "boolean",
  "date",
  "select",
  "multiselect",
  "image",
  "images",
  "file",
  "files",
];

const ATTRIBUTE_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatAttrDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type AttributeSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "code_asc"
  | "code_desc"
  | "sort_asc"
  | "sort_desc";

type OptionRow = { id: ID; label: string; value: string };

const Attributes = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "catalog_attribute_can_view");
  const canCreate = can(user, "catalog_attribute_can_create");
  const canUpdate = can(user, "catalog_attribute_can_update");
  const canDelete = can(user, "catalog_attribute_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<AttributeSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [filterGroupId, setFilterGroupId] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [onlyRequired, setOnlyRequired] = useState(false);
  const [onlyListing, setOnlyListing] = useState(false);
  const [onlyFilterable, setOnlyFilterable] = useState(false);
  const [onlyInvoice, setOnlyInvoice] = useState(false);
  const [onlyDeliveryChecklist, setOnlyDeliveryChecklist] = useState(false);
  const [onlyGatePass, setOnlyGatePass] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogAttributeRecord | null>(null);
  const [groupId, setGroupId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<AttributeType>("text");
  const [isRequired, setIsRequired] = useState(false);
  const [isListing, setIsListing] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isInvoice, setIsInvoice] = useState(false);
  const [isDeliveryChecklist, setIsDeliveryChecklist] = useState(false);
  const [isGatePass, setIsGatePass] = useState(false);
  const [allowRuntimeAdd, setAllowRuntimeAdd] = useState(false);
  const [sortOrder, setSortOrder] = useState("1");
  const [accept, setAccept] = useState("");
  const [maxSizeMb, setMaxSizeMb] = useState("");
  const [maxFiles, setMaxFiles] = useState("");
  const [optionRows, setOptionRows] = useState<OptionRow[]>([]);
  const [parentAttributeId, setParentAttributeId] = useState<string>("__none__");
  const [visibilityOperator, setVisibilityOperator] = useState<"in" | "not_in">("not_in");
  const [visibilityOptionValues, setVisibilityOptionValues] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<CatalogAttributeRecord | null>(null);
  const [viewAttr, setViewAttr] = useState<CatalogAttributeRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const groups = useMemo(() => listAttributeGroupRecords(), [version]);
  const attrs = useMemo(() => listCatalogAttributeRecords(), [version]);
  const groupCount = useMemo(() => groups.length, [groups]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFromApi = async () => {
      setIsForbidden(false);
      try {
        await syncCatalogAttributesFromApi();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Attributes] Attributes sync failed", error);
        if (mounted) {
          toast.error("Could not fetch latest attributes. Showing cached data.");
        }
      }
      try {
        await syncAttributeGroupsFromApi();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          if (mounted) {
            toast.error("Could not refresh attribute groups (permission denied). Group names may be cached.");
          }
        } else {
          console.error("[Attributes] Attribute groups sync failed", error);
        }
      }
      if (mounted) {
        refresh();
      }
    };
    void loadFromApi();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return attrs.filter((a) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(a.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(a.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const nameNeedle = filterName.trim().toLowerCase();
      if (nameNeedle && !a.name.toLowerCase().includes(nameNeedle)) return false;
      const codeNeedle = filterCode.trim().toLowerCase();
      if (codeNeedle && !a.code.toLowerCase().includes(codeNeedle)) return false;
      const matchesQuery =
        !q ||
        a.name.toLowerCase().includes(q) ||
        a.code.toLowerCase().includes(q) ||
        groups.find((g) => g.id === a.attributeGroupId)?.name.toLowerCase().includes(q);
      const matchesGroup = filterGroupId === "all" || a.attributeGroupId === filterGroupId;
      const matchesType = filterType === "all" || a.type === filterType;
      const matchesRequired = !onlyRequired || Boolean(a.isRequired);
      const matchesListing = !onlyListing || Boolean(a.isListing);
      const matchesFilterable = !onlyFilterable || Boolean(a.isFilter);
      const matchesInvoice = !onlyInvoice || Boolean(a.isInvoice);
      const matchesDeliveryChecklist = !onlyDeliveryChecklist || Boolean(a.isDeliveryChecklist);
      const matchesGatePass = !onlyGatePass || Boolean(a.isGatePass);
      return matchesQuery && matchesGroup && matchesType && matchesRequired && matchesListing && matchesFilterable && matchesInvoice && matchesDeliveryChecklist && matchesGatePass;
    });
  }, [
    attrs,
    groups,
    query,
    filterGroupId,
    filterType,
    onlyRequired,
    onlyListing,
    onlyFilterable,
    onlyInvoice,
    onlyDeliveryChecklist,
    onlyGatePass,
    filterCreatedDate,
    quickDate,
    filterName,
    filterCode,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "code_desc":
          return b.code.localeCompare(a.code);
        case "code_asc":
          return a.code.localeCompare(b.code);
        case "sort_desc":
          return (b.sortOrder ?? 0) - (a.sortOrder ?? 0);
        case "sort_asc":
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterGroupId !== "all" ? 1 : 0) +
    (filterType !== "all" ? 1 : 0) +
    (onlyRequired ? 1 : 0) +
    (onlyListing ? 1 : 0) +
    (onlyFilterable ? 1 : 0) +
    (onlyInvoice ? 1 : 0) +
    (onlyDeliveryChecklist ? 1 : 0) +
    (onlyGatePass ? 1 : 0) +
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterCode.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterGroupId("all");
    setFilterType("all");
    setOnlyRequired(false);
    setOnlyListing(false);
    setOnlyFilterable(false);
    setOnlyInvoice(false);
    setOnlyDeliveryChecklist(false);
    setOnlyGatePass(false);
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterCode("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterGroupId}|${filterType}|${onlyRequired ? "1" : "0"}|${onlyListing ? "1" : "0"}|${onlyFilterable ? "1" : "0"}|${onlyInvoice ? "1" : "0"}|${onlyDeliveryChecklist ? "1" : "0"}|${onlyGatePass ? "1" : "0"}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterCode}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: ATTRIBUTE_PAGE_SIZE_OPTIONS,
    defaultPageSize: ATTRIBUTE_PAGE_SIZE_OPTIONS[0],
  });

  const groupName = (id: ID) => groups.find((g) => g.id === id)?.name ?? "—";

  const parentCandidates = useMemo(
    () =>
      attrs.filter(
        (a) =>
          (a.type === "select" || a.type === "multiselect") && a.id !== editing?.id,
      ),
    [attrs, editing?.id],
  );

  const selectedParentAttribute = useMemo(
    () => parentCandidates.find((a) => a.id === parentAttributeId) ?? null,
    [parentCandidates, parentAttributeId],
  );

  const startCreate = () => {
    setEditing(null);
    setGroupId(groups[0]?.id ?? "");
    setCode("");
    setName("");
    setType("text");
    setIsRequired(false);
    setIsListing(false);
    setIsFilter(false);
    setIsInvoice(false);
    setIsDeliveryChecklist(false);
    setIsGatePass(false);
    setAllowRuntimeAdd(false);
    setSortOrder("1");
    setAccept("");
    setMaxSizeMb("");
    setMaxFiles("");
    setOptionRows([]);
    setParentAttributeId("__none__");
    setVisibilityOperator("not_in");
    setVisibilityOptionValues([]);
    setOpen(true);
  };

  const startEdit = (a: CatalogAttributeRecord) => {
    setEditing(a);
    setGroupId(a.attributeGroupId);
    setCode(a.code);
    setName(a.name);
    setType(a.type);
    setIsRequired(Boolean(a.isRequired));
    setIsListing(Boolean(a.isListing));
    setIsFilter(Boolean(a.isFilter));
    setIsInvoice(Boolean(a.isInvoice));
    setIsDeliveryChecklist(Boolean(a.isDeliveryChecklist));
    setIsGatePass(Boolean(a.isGatePass));
    setAllowRuntimeAdd(Boolean(a.allowRuntimeAdd));
    setSortOrder(String(a.sortOrder ?? 0));
    setAccept(a.accept ?? "");
    setMaxSizeMb(a.maxSizeMb != null ? String(a.maxSizeMb) : "");
    setMaxFiles(a.maxFiles != null ? String(a.maxFiles) : "");
    setOptionRows(
      (a.options ?? []).map((o) => ({
        id: o.id || crypto.randomUUID(),
        label: o.label,
        value: slugify(o.label) || o.value,
      })),
    );
    setParentAttributeId(a.parentAttributeId ?? "__none__");
    setVisibilityOperator(a.visibilityRule?.operator ?? "not_in");
    setVisibilityOptionValues(a.visibilityRule?.parentOptionValues ?? []);
    setOpen(true);
  };

  const needsOptions = type === "select" || type === "multiselect";
  const needsFileMeta = type === "file" || type === "files" || type === "image" || type === "images";
  const showAllowRuntimeAdd = type === "select";

  const handleNameChange = (nextName: string) => {
    setName(nextName);
    if (!editing) {
      setCode(slugify(nextName));
    }
  };

  const handleTypeChange = (nextType: AttributeType) => {
    setType(nextType);
    if (nextType !== "select") {
      setAllowRuntimeAdd(false);
    }
  };

  const save = async () => {
    if (isSaving) return;
    if (!groupId) {
      toast.error("Please select an attribute group.");
      return;
    }
    if (!name.trim()) {
      toast.error("Attribute label is required.");
      return;
    }
    if (!code.trim()) {
      toast.error("Attribute code is required.");
      return;
    }
    if (parentAttributeId !== "__none__" && visibilityOptionValues.length === 0) {
      toast.error("Select at least one parent option for conditional visibility.");
      return;
    }
    const slugCode = slugify(code);
    if (codeExists(slugCode, editing?.id)) {
      setInfoDialog({
        title: "Code already in use",
        description: "Another attribute already uses this code. Choose a different code.",
      });
      return;
    }
    const options =
      needsOptions && optionRows.length
        ? optionRows
            .filter((r) => r.label.trim())
            .map((r, idx) => ({
              id: r.id,
              label: r.label.trim(),
              value: slugify(r.label) || `opt-${idx}`,
              sortOrder: idx,
            }))
        : undefined;

    const payload = {
      attributeGroupId: groupId,
      code: slugCode,
      name: name.trim(),
      type,
      isRequired,
      isListing,
      isFilter,
      isInvoice,
      isDeliveryChecklist,
      isGatePass,
      allowRuntimeAdd: showAllowRuntimeAdd ? allowRuntimeAdd : false,
      sortOrder: Number(sortOrder) || 0,
      options,
      accept: needsFileMeta && accept.trim() ? accept.trim() : undefined,
      maxSizeMb: needsFileMeta && maxSizeMb.trim() ? Number(maxSizeMb) : undefined,
      maxFiles: type === "images" || type === "files" ? (maxFiles.trim() ? Number(maxFiles) : undefined) : undefined,
      parentAttributeId: parentAttributeId !== "__none__" ? parentAttributeId : null,
      visibilityRule:
        parentAttributeId !== "__none__" && visibilityOptionValues.length > 0
          ? {
              operator: visibilityOperator,
              parentOptionValues: visibilityOptionValues,
            }
          : undefined,
    };

    setIsSaving(true);
    try {
      if (editing) {
        const updateResult = await updateCatalogAttribute(editing.id, payload);
        toast.success(updateResult?.message || "Attribute updated successfully.");
      } else {
        const createResult = await createCatalogAttribute(payload);
        toast.success(createResult.message);
      }
      setOpen(false);
      refresh();
    } catch (error) {
      console.error("[Attributes] Save failed", error);
      toast.error("Unable to save attribute. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = (a: CatalogAttributeRecord) => setDeleteTarget(a);

  const confirmRemoveAttribute = async () => {
    if (!deleteTarget) return;
    try {
      removeLinksForAttribute(deleteTarget.id);
      const result = await deleteCatalogAttribute(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Attributes] Delete failed", error);
      toast.error("Unable to delete attribute. Please try again.");
    }
  };

  const addOptionRow = () => {
    setOptionRows((r) => [...r, { id: crypto.randomUUID(), label: "", value: "" }]);
  };

  return isForbidden ? (
    <PermissionDenied
      title="Attributes access required"
      message="You don’t have permission to view catalog attributes. Ask an administrator to grant catalog_attribute_can_view_any."
    />
  ) : (
    <Layout title="Attributes">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Attributes</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Fields inside each group. Manage groups on{" "}
              <Link to="/attribute-groups" className="text-primary hover:underline font-medium">
                Attribute groups
              </Link>
              , then assign fields to categories on{" "}
              <Link to="/categories" className="text-primary hover:underline font-medium">
                Categories
              </Link>
              .
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate} disabled={!groupCount}>
              <Plus className="w-4 h-4" /> Add attribute
            </Button>
          ) : null}
        </div>

        {!groupCount ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            Create at least one attribute group before adding attributes.{" "}
            <Link to="/attribute-groups" className="text-primary font-medium hover:underline">
              Go to Attribute groups
            </Link>
            .
          </div>
        ) : null}

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, group…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter attributes</SheetTitle>
                  <SheetDescription>Refine by label, code, group, type, flags, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Attribute</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Engine"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Code contains</Label>
                      <Input
                        className="glass-input h-10 font-mono text-sm"
                        value={filterCode}
                        onChange={(e) => setFilterCode(e.target.value)}
                        placeholder="e.g. engine_cc"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Group</Label>
                    <Select value={filterGroupId} onValueChange={setFilterGroupId}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All groups" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All groups</SelectItem>
                        {groups.map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        {ATTRIBUTE_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Required only</span>
                      <Checkbox checked={onlyRequired} onCheckedChange={(c) => setOnlyRequired(Boolean(c))} />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Show in listing only</span>
                      <Checkbox checked={onlyListing} onCheckedChange={(c) => setOnlyListing(Boolean(c))} />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Show in filters only</span>
                      <Checkbox checked={onlyFilterable} onCheckedChange={(c) => setOnlyFilterable(Boolean(c))} />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Show on invoice only</span>
                      <Checkbox checked={onlyInvoice} onCheckedChange={(c) => setOnlyInvoice(Boolean(c))} />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Show on delivery checklist only</span>
                      <Checkbox checked={onlyDeliveryChecklist} onCheckedChange={(c) => setOnlyDeliveryChecklist(Boolean(c))} />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Show on Gate Pass only</span>
                      <Checkbox checked={onlyGatePass} onCheckedChange={(c) => setOnlyGatePass(Boolean(c))} />
                    </label>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="attr-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={ATTRIBUTE_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as AttributeSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="code_asc">Code: A to Z</SelectItem>
                  <SelectItem value="code_desc">Code: Z to A</SelectItem>
                  <SelectItem value="sort_asc">Sort order: Low to high</SelectItem>
                  <SelectItem value="sort_desc">Sort order: High to low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="attr-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={ATTRIBUTE_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AttributeSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="code_asc">Code: A to Z</SelectItem>
                    <SelectItem value="code_desc">Code: Z to A</SelectItem>
                    <SelectItem value="sort_asc">Sort order: Low to high</SelectItem>
                    <SelectItem value="sort_desc">Sort order: High to low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit attribute" : "New attribute"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-1">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Group *</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Label *</Label>
                  <Input
                    className="glass-input h-10"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Code *</Label>
                  <Input
                    className="glass-input h-10 font-mono text-sm bg-muted/30"
                    value={code}
                    readOnly
                    placeholder="auto-generated"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Sort order</Label>
                  <Input className="glass-input h-10" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <Select value={type} onValueChange={(v) => handleTypeChange(v as AttributeType)}>
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ATTRIBUTE_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                  <span>Required</span>
                  <Checkbox checked={isRequired} onCheckedChange={(c) => setIsRequired(Boolean(c))} />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                  <span>Show in listing</span>
                  <Checkbox checked={isListing} onCheckedChange={(c) => setIsListing(Boolean(c))} />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                  <span>Show in filters</span>
                  <Checkbox checked={isFilter} onCheckedChange={(c) => setIsFilter(Boolean(c))} />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                  <span>Show on invoice</span>
                  <Checkbox checked={isInvoice} onCheckedChange={(c) => setIsInvoice(Boolean(c))} />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                  <span>Show on delivery checklist</span>
                  <Checkbox checked={isDeliveryChecklist} onCheckedChange={(c) => setIsDeliveryChecklist(Boolean(c))} />
                </label>
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                  <span>Show on Gate Pass</span>
                  <Checkbox checked={isGatePass} onCheckedChange={(c) => setIsGatePass(Boolean(c))} />
                </label>
                {showAllowRuntimeAdd ? (
                  <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
                    <span>Allow run time add</span>
                    <Checkbox checked={allowRuntimeAdd} onCheckedChange={(c) => setAllowRuntimeAdd(Boolean(c))} />
                  </label>
                ) : (
                  <div className="hidden sm:block" aria-hidden />
                )}
              </div>

              {needsOptions ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Options</Label>
                    <Button type="button" size="sm" variant="glass" className="h-7 text-xs" onClick={addOptionRow}>
                      + Option
                    </Button>
                  </div>
                  <div className="space-y-2 rounded-lg border border-border/50 p-2 bg-muted/10">
                    {optionRows.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2 text-center">No options yet</p>
                    ) : (
                      optionRows.map((row, idx) => (
                        <div key={row.id} className="flex gap-2 items-center">
                          <Input
                            className="glass-input h-9 text-sm"
                            placeholder="Label"
                            value={row.label}
                            onChange={(e) => {
                              const label = e.target.value;
                              const next = [...optionRows];
                              next[idx] = { ...row, label, value: slugify(label) };
                              setOptionRows(next);
                            }}
                          />
                          <Input
                            className="glass-input h-9 bg-muted/30 text-sm font-mono"
                            placeholder="auto-generated"
                            value={row.value}
                            readOnly
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 shrink-0 text-destructive"
                            onClick={() => setOptionRows(optionRows.filter((_, i) => i !== idx))}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : null}

              <div className="space-y-3 rounded-lg border border-border/50 bg-muted/10 p-3 mt-0">
                <div>
                  <Label className="text-xs text-muted-foreground">Conditional visibility</Label>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Show this field only when a parent select/multiselect option matches the rule below.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Parent attribute</Label>
                  <Select value={parentAttributeId} onValueChange={setParentAttributeId}>
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Always visible" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Always visible</SelectItem>
                      {parentCandidates.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} ({groupName(a.attributeGroupId)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {parentAttributeId !== "__none__" ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Show when parent value</Label>
                      <Select
                        value={visibilityOperator}
                        onValueChange={(v) => setVisibilityOperator(v as AttributeVisibilityOperator)}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in">Is one of</SelectItem>
                          <SelectItem value="not_in">Is not one of</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Parent options</Label>
                      {!selectedParentAttribute?.options?.length ? (
                        <p className="text-xs text-muted-foreground">Selected parent has no options.</p>
                      ) : (
                        <div className="space-y-2 rounded-md border border-border/40 bg-background/60 p-2">
                          {(selectedParentAttribute.options ?? []).map((opt) => {
                            const value = String(opt.value ?? "");
                            if (!value) return null;
                            const checked = visibilityOptionValues.includes(value);
                            return (
                              <label key={opt.id} className="flex cursor-pointer items-center gap-2 text-sm">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(next) => {
                                    setVisibilityOptionValues((prev) =>
                                      next
                                        ? [...prev, value]
                                        : prev.filter((v) => v !== value),
                                    );
                                  }}
                                />
                                <span>{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>

              {needsFileMeta ? (
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Accept (MIME pattern)</Label>
                    <Input
                      className="glass-input h-10 text-sm"
                      value={accept}
                      onChange={(e) => setAccept(e.target.value)}
                      placeholder="image/* or application/pdf"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Max size (MB)</Label>
                      <Input className="glass-input h-10" value={maxSizeMb} onChange={(e) => setMaxSizeMb(e.target.value)} type="number" />
                    </div>
                    {(type === "images" || type === "files") && (
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Max files</Label>
                        <Input className="glass-input h-10" value={maxFiles} onChange={(e) => setMaxFiles(e.target.value)} type="number" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewAttr} onOpenChange={(o) => !o && setViewAttr(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Attribute details</DialogTitle>
            </DialogHeader>
            {viewAttr ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Label</dt>
                  <dd className="font-medium text-foreground">{viewAttr.name}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Code</dt>
                  <dd className="text-muted-foreground font-mono">{viewAttr.code}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Group</dt>
                  <dd className="text-muted-foreground">{groupName(viewAttr.attributeGroupId)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Type</dt>
                  <dd className="text-muted-foreground">{viewAttr.type}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Sort order</dt>
                  <dd className="text-muted-foreground">{viewAttr.sortOrder ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Flags</dt>
                  <dd className="text-muted-foreground">
                    {[
                      viewAttr.isRequired ? "Required" : null,
                      viewAttr.isListing ? "Listing" : null,
                      viewAttr.isFilter ? "Filter" : null,
                      viewAttr.isInvoice ? "Invoice" : null,
                      viewAttr.isDeliveryChecklist ? "Delivery Checklist" : null,
                      viewAttr.isGatePass ? "Gate Pass" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </dd>
                </div>
                {viewAttr.type === "select" || viewAttr.type === "multiselect" ? (
                  <div>
                    <dt className="text-xs text-muted-foreground">Options</dt>
                    <dd className="text-muted-foreground">{(viewAttr.options ?? []).length}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatAttrDate(viewAttr.createdAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewAttr(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {groupCount && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {attrs.length === 0
              ? canCreate
                ? "No attributes yet. Click “Add attribute” to create one."
                : "No attributes yet."
              : "No attributes match your search."}
          </div>
        ) : null}

        {groupCount && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={a.name}
                  primaryMeta={{
                    label: "Group",
                    value: groupName(a.attributeGroupId),
                    icon: CircleDot,
                    iconClassName: "text-red-600",
                  }}
                  secondaryMeta={{
                    label: "Type",
                    value: a.type ?? "—",
                    icon: Braces,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`Code: ${a.code}`}
                  footerRight={`Added: ${formatCatalogAuditDate(a.createdAt)}`}
                  onView={canView ? () => setViewAttr(a) : undefined}
                  onEdit={canUpdate ? () => startEdit(a) : undefined}
                  onDelete={canDelete ? () => requestRemove(a) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {groupCount && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Braces className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{a.name}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600">
                        {a.type ?? "—"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate font-mono">{a.code}</p>
                    <p className="text-xs text-muted-foreground mt-1">{groupName(a.attributeGroupId)}</p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-[11px] text-muted-foreground">Added {formatCatalogAuditDate(a.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewAttr(a)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => startEdit(a)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => requestRemove(a)}
                        aria-label="Delete attribute"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {groupCount && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this attribute?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? It will be removed from
                all categories. This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmRemoveAttribute()}
        />

        <ConfirmAlertDialog
          open={!!infoDialog}
          onOpenChange={(o) => !o && setInfoDialog(null)}
          title={infoDialog?.title ?? ""}
          description={infoDialog?.description ?? ""}
          mode="alert"
          variant="warning"
          confirmLabel="OK"
          onConfirm={() => setInfoDialog(null)}
        />
      </div>
    </Layout>
  );
};

export default Attributes;
